import {ChangeDetectorRef, Component, CUSTOM_ELEMENTS_SCHEMA, OnInit} from '@angular/core';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {FieldType} from '@ngx-formly/core';

import {ConfiguratorProductService} from '@app/product/configurator/services/configurator-product.service';
import {DcpConfiguratorFormService} from '@app/product/configurator/services/dcp-configurator-form.service';
import {DatePickerViewModel} from '@app/product/models/date-picker-view.model';
import {ContractDurationService} from '@app/services/contract-duration-service';
import {DcpCartDecoratorService} from '@app/services/dcp-cart-decorator.service';
import {DATE_FORMAT_DISPLAY_LONG, DATE_FORMAT_ISO} from '@app/services/date/date.service';
import {LanguageSupportService} from '@app/services/i18n/language-support.service';
import {TransactionTypeService} from '@app/services/transactionTypes/transaction-type.service';
import {
  ContractDuration,
  DateService,
  DcpPriceData,
  DcpStartDatePeriod,
  IconButtonTooltipComponent,
  ValidationService
} from '@dcp/configurator';
import {ActiveCartFacade, OrderEntry} from '@spartacus/cart/base/root';
import {addDays, addMonths, addYears, format, isValid, Locale, parse, parseISO, subDays} from 'date-fns';
import {BehaviorSubject, Observable} from 'rxjs';
import {map, switchMap} from 'rxjs/operators';
import {AsyncPipe} from '@angular/common';
import {LanguageService, TranslatePipe} from '@spartacus/core';

@UntilDestroy()
@Component({
  selector: 'dcp-field-date-picker',
  templateUrl: './formly-field-date-picker.component.html',
  styleUrls: ['./formly-field-date-picker.component.scss'],
  imports: [IconButtonTooltipComponent, AsyncPipe, TranslatePipe],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class FormlyFieldDatePickerComponent extends FieldType implements OnInit {
  public dates: DatePickerViewModel = {};
  public isDatepickerDisabled$ = new BehaviorSubject(false);
  private selectedContractDuration: ContractDuration;
  private priceData: DcpPriceData;
  public dateFormat: string = DATE_FORMAT_DISPLAY_LONG;
  public dateFormatISO: string = DATE_FORMAT_ISO;
  private locale: Locale;

  endDateTooltipContent$: Observable<string>;

  constructor(
    private dcpCartDecoratorService: DcpCartDecoratorService,
    private dateService: DateService,
    public configuratorFormService: DcpConfiguratorFormService,
    private cd: ChangeDetectorRef,
    public validationService: ValidationService,
    private transactionTypeService: TransactionTypeService,
    private configuratorProductService: ConfiguratorProductService,
    private activeCartFacade: ActiveCartFacade,
    private contractDurationService: ContractDurationService,
    private languageService: LanguageService,
    private languageSupportService: LanguageSupportService,
  ) {
    super();
    this.locale = this.languageSupportService.resolveDateFnsLocale('en');
  }

  get minDate(): string {
    return this.getDateRestrictions('startDate');
  }

  get maxDate(): string {
    return this.getDateRestrictions('endDate');
  }

  ngOnInit(): void {
    this.resetValidators();
    this.isDatepickerDisabled$.next(true);
    this.subscribeToPriceData();
    this.subscribeToContractDuration();
    this.endDateTooltipContent$ = this.getEndDateTooltipContent$();
    this.languageService.getActive().pipe(untilDestroyed(this)).subscribe((iso) => {
      this.locale = this.languageSupportService.resolveDateFnsLocale(iso);
    });
  }

  subscribeToPriceData(): void {
    this.dcpCartDecoratorService.priceDataSubject.pipe(untilDestroyed(this)).subscribe((priceData) => {
      this.priceData = priceData;
      if (priceData?.configuration?.contract?.startDatePeriod) {
        this.dates = this.extractData(priceData);
        this.updateEndDate(priceData);
        this.isDatepickerDisabled$.next(false);
        this.setDatePickerValue(priceData.configuration.contract.startDatePeriod);
      } else {
        this.dates = {};
      }
      this.cd.detectChanges(); // needs to stay outside the if-clause to keep the tests working
    });
  }

  private setDatePickerValue(startDatePeriod: DcpStartDatePeriod) {
    if (startDatePeriod.startDate) {
      const currentValue = this.formControl.value
        ? (() => {
            const dateObject = this.dateService.localDateStructToDate(this.formControl.value);
            return isValid(dateObject) ? format(dateObject, this.dateFormatISO) : null;
          })()
        : null;
      if (
        !currentValue ||
        (this.minDate && currentValue < this.minDate) ||
        (this.maxDate && currentValue > this.maxDate)
      ) {
        this.formControl.setValue(this.dateService.stringToLocalDateStruct(startDatePeriod.startDate));
      }
    }
  }

  extractData(priceData: DcpPriceData): DatePickerViewModel {
    return {
      startDatePeriod: {
        startDate: priceData.configuration.contract.startDatePeriod.startDate
          ? parseISO(priceData.configuration.contract.startDatePeriod.startDate)
          : undefined,
        endDate: priceData.configuration.contract.startDatePeriod.endDate
          ? parseISO(priceData.configuration.contract.startDatePeriod.endDate)
          : undefined,
      },
    };
  }

  getDateRestrictions(date: 'startDate' | 'endDate'): string {
    const dateObject = this.dates.startDatePeriod?.[date];
    return dateObject && isValid(dateObject) ? format(dateObject, this.dateFormatISO) : '';
  }

  /**
   * Replaces the validators used by formly with the angular validators.
   * https://angular.io/api/forms/Validators
   *
   * This is needed because formly uses a global FormGroup.
   * The validators of this FormGroup are not reset when the form is destroyed in the view.
   *
   * When more than on item is in cart and multiple items are edited after another,
   * the lowes max value of the quantity date-picker will be set as a validator.
   * It will not be overwritten by a higher max value.
   * This leads to a failing validation with a valid start date.
   */
  private resetValidators() {
    this.configuratorFormService.resetValidators(this.field, this.formControl, []);
  }

  getEndDateTooltipContent$() {
    return this.configuratorProductService.getVariant$().pipe(
      switchMap((vp) => this.activeCartFacade.getEntry(vp.code)),
      map((cartEntry: OrderEntry) => this.transactionTypeService.getTransactionTypeCodeByUrlOrOrderEntry(cartEntry)),
      switchMap((ttCode) =>
        this.transactionTypeService.getTranslations(ttCode).pipe(map((translations) => translations?.endDateTooltip)),
      ),
    );
  }

  /**
   * @private
   * Subscribes to the contract's duration service observable
   */
  private subscribeToContractDuration() {
    this.contractDurationService.contractDuration$.pipe(untilDestroyed(this)).subscribe((contractDuration) => {
      this.selectedContractDuration = contractDuration;
      this.updateEndDate(this.priceData);
    });
  }

  /**
   * @private
   * Updates the end date of the contract duration based on the start date
   * The start date will be either current date or the form value
   */
  private updateEndDate(priceData: DcpPriceData) {
    if (priceData?.price?.endDate) {
      this.dates.endDate = priceData.price.endDate;
      return;
    }

    if (!this.selectedContractDuration?.value || !this.formControl.value) {
      this.dates.endDate = undefined;
      return;
    }

    const startDate = this.dateService.localDateStructToDate(this.formControl.value);
    const unit = this.getUnitFromSelectedContractDuration();
    const duration = parseInt(this.selectedContractDuration.value, 10);
    let endDate: Date;
    if (unit === 'month') {
      endDate = addMonths(subDays(startDate, 1), duration);
    } else if (unit === 'year') {
      endDate = addYears(subDays(startDate, 1), duration);
    } else {
      endDate = addDays(subDays(startDate, 1), duration);
    }
    this.dates.endDate = format(endDate, this.dateFormatISO);
  }

  /**
   * Gets a string representation of the contract duration metric
   */
  public getUnitFromSelectedContractDuration(): 'month' | 'year' | 'day' {
    if (this.selectedContractDuration?.metric?.id === 'month') {
      return 'month';
    } else if (this.selectedContractDuration?.metric?.id === 'year') {
      return 'year';
    } else if (this.selectedContractDuration?.metric?.id === 'day') {
      return 'day';
    }
    return 'month';
  }

  public getFormattedDate(): string {
    const value = this.formControl.value;
    if (!value) {
      return '';
    }
    const dateObject = this.dateService.localDateStructToDate(value);
    return isValid(dateObject) ? format(dateObject, this.dateFormat, {locale: this.locale}) : '';
  }

  public onSelectionChange(event: CustomEvent): void {
    const userInputDate = event.detail.selectedValues?.[0];
    if (!userInputDate) {
      this.formControl.setValue(null);
      return;
    }
    const parsed = parse(userInputDate, this.dateFormat, new Date(), {locale: this.locale});
    if (!isValid(parsed)) {
      this.formControl.setValue(null);
      return;
    }
    this.formControl.setValue(this.dateService.stringToLocalDateStruct(format(parsed, this.dateFormatISO)));
  }

  public parseDateToFormat(date: string): string {
    const parsed = parseISO(date);
    return isValid(parsed) ? format(parsed, this.dateFormat, {locale: this.locale}) : '';
  }
}
